<script setup lang="ts">
import SelectButton, { type SelectButtonContext } from 'primevue/selectbutton';
import PredicateValueDisplay, {
    type PredicateValueDisplayFormat,
} from './PredicateValueDisplay.vue';
import { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';
import { computed } from 'vue';

export type { PredicateValueDisplayFormat };

export type PredicateValueInputMethod = 'buttons' | 'dropdown' | 'radio';

const props = withDefaults(
    defineProps<{
        modelValue?: PredicateValue;
        readonly?: boolean;
        options?: PredicateValue[];
        labelFormat?: PredicateValueDisplayFormat;
        inputMethod?: PredicateValueInputMethod;
        inputId?: string;
        inputName?: string;
    }>(),
    {
        modelValue: () => PredicateValue.Indeterminate,
        options: () => [
            PredicateValue.Yes,
            PredicateValue.No,
            PredicateValue.Undecidable,
            PredicateValue.Indeterminate,
        ],
        labelFormat: 'answer',
        inputMethod: 'buttons',
        inputId: (props) => props?.inputName ?? 'predicate-value-input',
        inputName: (props) => props?.inputId ?? 'predicate-value-input',
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: PredicateValue): void;
}>();

const model = computed({
    get: () => props.modelValue,
    set: (newValue: PredicateValue | null) => {
        emit('update:modelValue', newValue ?? PredicateValue.Indeterminate);
    },
});

const valueClasses: Record<PredicateValue, { active: string; normal: string }> =
    {
        [PredicateValue.Yes]: {
            active: 'bg-green-300 border-green-500',
            normal: 'hover:bg-green-100 hover:border-green-200',
        },
        [PredicateValue.No]: {
            active: 'bg-pink-400 border-pink-500',
            normal: 'hover:bg-pink-100 hover:border-pink-200',
        },
        [PredicateValue.Undecidable]: {
            active: 'bg-yellow-500 border-yellow-500',
            normal: 'hover:bg-yellow-100 hover:border-yellow-300',
        },
        [PredicateValue.Indeterminate]: {
            active: 'bg-bluegray-200 border-bluegray-500',
            normal: 'hover:bg-bluegray-100 hover:border-bluegray-200',
        },
    };

const ptButton = ({ context }: { context: SelectButtonContext }) => {
    return {
        class: context.active
            ? valueClasses[context.option].active
            : valueClasses[context.option].normal,
    };
};
</script>

<template>
    <template v-if="readonly">
        <div class="predicate-value-input predicate-value-input-readonly">
            <PredicateValueDisplay
                v-bind="$attrs"
                :value="model"
                :format="labelFormat"
            />
        </div>
    </template>
    <template v-else-if="inputMethod === 'dropdown'">
        <Dropdown
            v-model="model"
            v-bind="$attrs"
            class="predicate-value-input predicate-value-input-dropdown"
            :options="options"
            :input-id="inputId"
            :input-name="inputName"
        >
            <template #value>
                <PredicateValueDisplay :value="model" :format="labelFormat" />
            </template>
            <template #option="{ option }">
                <PredicateValueDisplay :value="option" format="symbol" color />
                <PredicateValueDisplay :value="option" />
            </template>
        </Dropdown>
    </template>
    <template v-else-if="inputMethod === 'radio'">
        <div class="predicate-value-input predicate-value-input-radio-group">
            <div
                v-for="option in options"
                :key="option"
                class="flex align-items-center"
            >
                <RadioButton
                    v-model="model"
                    :input-id="`${inputId}-${option}`"
                    :name="inputName"
                    :value="option"
                />
                <PredicateValueDisplay
                    is="label"
                    class="predicate-value-input-radio-label"
                    :for="`${inputId}-${option}`"
                    :value="option"
                    :format="labelFormat"
                />
            </div>
        </div>
    </template>
    <template v-else>
        <SelectButton
            v-model="model"
            v-bind="$attrs"
            class="predicate-value-input predicate-value-input-buttons"
            :options="options"
            :pt="{ button: ptButton }"
        >
            <template #option="{ option }">
                <PredicateValueDisplay
                    :value="option"
                    :format="labelFormat"
                    class="p-button-label"
                />
            </template>
        </SelectButton>
    </template>
</template>

<style lang="scss">
.p-dropdown-item .predicate-value-symbol {
    width: 1.8rem;
    margin-left: -1em;
    text-align: center;
    font-weight: 1000;
    display: inline-block;
}

.predicate-value-input-radio-label {
    margin-left: 0.5rem;
    padding: 0.25rem;
}

.predicate-value-input-readonly {
    display: flex;
    padding: 0.75rem;
    font-weight: bold;
    align-items: center !important;
    border-radius: var(--border-radius);
}
</style>
